// PC NU20 scene layout, verified against local TCS files and BrickBench's
// format readers. This parser is read-only and does not execute game code.
export function parseGSC(source) {
  if (!(source instanceof ArrayBuffer) || source.byteLength < 64 || source.byteLength > 512 * 1024 * 1024) {
    throw new Error('Choose a PC GSC scene between 64 bytes and 512 MiB.');
  }
  const buffer = source.slice(0);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  function range(offset, size) {
    if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(size) || offset < 0 || size < 0 || offset + size > bytes.length) {
      throw new Error(`Scene data is out of bounds at 0x${offset.toString(16)} (${size} bytes).`);
    }
    return offset;
  }
  const i32 = (o) => view.getInt32(range(o, 4), true);
  const u32 = (o) => view.getUint32(range(o, 4), true);
  const u16 = (o) => view.getUint16(range(o, 2), true);
  const i16 = (o) => view.getInt16(range(o, 2), true);
  const f32 = (o) => view.getFloat32(range(o, 4), true);
  const fourcc = (o) => String.fromCharCode(...bytes.subarray(range(o, 4), o + 4));
  function string(o) {
    if (!o) return '';
    range(o, 1); let end = o;
    while (end < bytes.length && end - o < 512 && bytes[end]) end++;
    if (end === bytes.length || end - o === 512) throw new Error('Invalid scene object name.');
    return new TextDecoder().decode(bytes.subarray(o, end));
  }
  function count(o, maximum = 1000000) {
    const n = i32(o);
    if (n < 0 || n > maximum) throw new Error(`Invalid scene count ${n} at 0x${o.toString(16)}.`);
    return n;
  }
  const nu = i32(0) + 4;
  if (fourcc(nu) !== 'NU20') throw new Error('This is not the supported Complete Saga PC NU20 scene format.');
  const pointers = nu + 0x18 + i32(nu + 0x18);
  const header = nu + 0x1c + i32(nu + 0x1c);
  if (fourcc(header - 8) !== 'GSNH') throw new Error('Missing game-scene header.');
  if (fourcc(pointers - 8) !== 'PNTR') throw new Error('Missing game-scene pointer table.');
  const pointerCount = count(pointers);
  let externalPointers = 0;
  range(pointers + 4, pointerCount * 4);
  for (let n = 0; n < pointerCount; n++) {
    const entry = pointers + 4 + n * 4;
    const target = entry + i32(entry);
    const relative = i32(target);
    if (relative) {
      // Some spline/resource slots retain process addresses. Relocate their
      // 32-bit values, but only dereference validated file ranges below.
      const relocated = (target + relative) | 0;
      if (relocated < 0 || relocated >= bytes.length) externalPointers++;
      view.setInt32(target, relocated, true);
    }
  }
  const blocks = new Map();
  for (let o = nu + 0x20; o + 8 <= bytes.length;) {
    const name = fourcc(o);
    if (name === 'NUS:') break;
    const size = u32(o + 4);
    if (size < 8) throw new Error(`Invalid ${name} block size.`);
    range(o, size);
    blocks.set(name, { offset: o + 8, size: size - 8 });
    o += size;
    // PNTR terminates the serialized blocks. Several shipped scenes append
    // an untagged "Converted:" build footer instead of a NUS: marker.
    if (name === 'PNTR') break;
  }
  // PC spline records live in SST0. Their name handles intentionally retain
  // encoded upper bits; BrickBench's TCS NameTable/Spline readers resolve the
  // low 16-bit offset after relocation, rather than dereferencing that handle.
  const splines = new Map(), splineRecords = [], unresolvedSplineNames = [], nameTableDiagnostics = [];
  const splineBlock = blocks.get('SST0'), nameBlock = blocks.get('NTBL');
  if (splineBlock) {
    if (!nameBlock) throw new Error('Scene splines require the original name table.');
    const names = new Map();
    count(nameBlock.offset, nameBlock.size);
    // NTBL's leading size is not consistently the exact C-string byte extent:
    // shipped title/hub files include nonzero alignment bytes after the last
    // name. Bound string traversal by the serialized block, as the reference
    // reader does, rather than cutting a padding entry at the declared size.
    const nameStart = nameBlock.offset + 4, nameEnd = nameBlock.offset + nameBlock.size;
    for (let at = nameStart; at < nameEnd;) {
      let end = at;
      while (end < nameEnd && bytes[end] !== 0) end++;
      if (end === nameEnd) {
        // Factory_E and AsteroidChase_D each ship one nonzero, unreferenced
        // alignment byte after their last terminated name. Preserve a bounded
        // diagnostic, but never accept an unterminated referenced string.
        for (let p = 0; p < pointerCount; p++) {
          const entry = pointers + 4 + p * 4, target = entry + i32(entry), value = i32(target);
          if (value >= at && value < nameEnd) throw new Error('Unterminated referenced scene name table entry.');
        }
        nameTableDiagnostics.push({ kind: 'unreferenced-unterminated-tail', offset: at,
          length: nameEnd - at, bytes: Array.from(bytes.subarray(at, Math.min(nameEnd, at + 32))) });
        break;
      }
      names.set(at, new TextDecoder().decode(bytes.subarray(at, end)));
      at = end + 1;
    }
    const splineCount = count(splineBlock.offset, 65535), splineEnd = splineBlock.offset + splineBlock.size;
    let at = splineBlock.offset + 8;
    for (let n = 0; n < splineCount; n++) {
      if (at + 8 > splineEnd) throw new Error('Spline header exceeds its SST0 block.');
      const address = at, pointCount = i16(at), nameHandle = i32(at + 4);
      if (pointCount < 0 || at + 8 + pointCount * 12 > splineEnd) throw new Error('Spline points exceed their SST0 block.');
      const nameAddress = nameBlock.offset + ((nameHandle - nameBlock.offset + 0x2c) & 0xffff);
      if (nameHandle !== 0 && nameTableDiagnostics.some(tail => nameAddress >= tail.offset && nameAddress < tail.offset + tail.length))
        throw new Error('Unterminated referenced scene spline name table entry.');
      const resolvedName = nameHandle === 0 ? undefined : names.get(nameAddress), name = resolvedName === undefined ? null : resolvedName.toLowerCase();
      at += 8;
      const points = [];
      for (let p = 0; p < pointCount; p++, at += 12) {
        const point = { x: f32(at), y: f32(at + 4), z: f32(at + 8) };
        if (!Object.values(point).every(Number.isFinite)) throw new Error('Invalid nonfinite scene spline point.');
        points.push(point);
      }
      const spline = { name, address, nameHandle, nameAddress, points };
      splineRecords.push(spline);
      if (name === null) unresolvedSplineNames.push({ index: n, address, nameHandle, nameAddress });
      // NuSplineFind selects the first matching name. Preserve every record
      // separately so duplicate/unnamed splines never silently lose geometry.
      else if (!splines.has(name)) splines.set(name, spline);
    }
  }
  const textures = new Map();
  const textureCount = count(header + 4, 65535);
  const descriptors = i32(header + 8);
  range(descriptors, textureCount * 4);
  let payload = 6;
  for (let n = 0; n < textureCount; n++) {
    const descriptor = i32(descriptors + n * 4);
    if (!i32(descriptor) || !i32(descriptor + 4)) continue;
    const width = Math.abs(i32(payload)), height = Math.abs(i32(payload + 4)), size = u32(payload + 20);
    if (!width || !height || width > 16384 || height > 16384) throw new Error('Invalid scene texture dimensions.');
    range(payload + 24, size);
    textures.set(n, { width, height, data: buffer.slice(payload + 24, payload + 24 + size) });
    payload += 24 + size;
  }
  const materials = new Map();
  const ms = blocks.get('MS00');
  if (!ms) throw new Error('Scene has no material block.');
  const materialCount = count(ms.offset, 65535);
  range(ms.offset + 8, materialCount * 0x2c4);
  for (let n = 0; n < materialCount; n++) {
    const o = ms.offset + 8 + n * 0x2c4;
    materials.set(o, {
      address: o, texture: i16(o + 0x74), alpha: u32(o + 0x40),
      color: Array.from({ length: 4 }, (_, c) => f32(o + 0x54 + c * 4)),
      format: u32(o + 0x1f0), surfaceUV: view.getUint8(o + 0x15d),
    });
  }
  const meshHeader = i32(header + 0x1d0);
  const meshList = i32(meshHeader + 0x10), meshCount = count(meshHeader + 0x14);
  range(meshList, meshCount * 4);
  const meshes = new Map();
  for (let n = 0; n < meshCount; n++) {
    const o = i32(meshList + n * 4);
    range(o, 0x38);
    meshes.set(o, {
      address: o, type: u32(o), triangles: count(o + 4), stride: u16(o + 8),
      vertexOffset: u32(o + 0x14), vertices: count(o + 0x18), indexOffset: u32(o + 0x1c),
      indexBuffer: u32(o + 0x20), vertexBuffer: u32(o + 0x24), dynamic: u32(o + 0x28),
    });
  }
  const vertexBuffers = [], indexBuffers = [];
  for (const collection of [vertexBuffers, indexBuffers]) {
    const n = u16(payload); payload += 2;
    if (n > 4096) throw new Error('Too many scene buffers.');
    for (let b = 0; b < n; b++) {
      const size = u32(payload); payload += 4;
      range(payload, size);
      collection.push({ offset: payload, size });
      payload += size;
    }
  }
  const disp = blocks.get('DISP');
  if (!disp) throw new Error('Scene has no display lists.');
  const commandStart = i32(disp.offset + 8);
  const commandCount = count(disp.offset + 4);
  range(commandStart, commandCount * 16);
  const commands = new Map();
  for (let n = 0; n < commandCount; n++) {
    const o = commandStart + n * 16;
    commands.set(o, { type: view.getUint8(o), flags: view.getUint8(o + 1), resource: i32(o + 4) });
  }
  const identity = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  const draws = [], specialObjects = [];
  function execute(start, override = null, maxCommands = commandCount) {
    let material = null, matrix = override || identity;
    for (let n = 0; n < maxCommands; n++) {
      const command = commands.get(start + n * 16);
      if (!command) throw new Error('Display list references a missing command.');
      if (command.type === 0x8e || command.type === 0x84) break;
      if (command.flags === 0 || command.flags === 3) {
        if (command.type === 0x80) material = materials.get(command.resource);
        if (command.type === 0x83 && !override) {
          matrix = Array.from({ length: 16 }, (_, c) => f32(command.resource + c * 4));
        }
        if (command.type === 0x82) {
          const mesh = meshes.get(command.resource);
          if (!mesh || !material) throw new Error('Draw references an unknown mesh or material.');
          draws.push({ mesh, material, matrix: matrix.slice() });
        }
      }
      if (command.flags === 4) break;
    }
  }
  const staticCount = count(disp.offset + 0x60), staticStart = i32(disp.offset + 0x64);
  range(staticStart, staticCount * 0x24);
  for (let n = 0; n < staticCount; n++) execute(i32(staticStart + n * 0x24 + 4));
  // Special objects reference model parts separately from static display lists.
  const modelCount = count(disp.offset + 0x10), modelStart = i32(disp.offset + 0x14);
  const specialCount = count(disp.offset + 0x6c), specialStart = i32(disp.offset + 0x70);
  const materialArray = [...materials.values()];
  range(modelStart, modelCount * 12); range(specialStart, specialCount * 0xd0);
  for (let n = 0; n < specialCount; n++) {
    const o = specialStart + n * 0xd0;
    // Same PC display-special flags consumed by NuSceneRenderInternal:
    // bit 1 is visible. Gizmo scripts can change it after scene load.
    const flags = u32(o + 0xb8);
    const matrix = Array.from({ length: 16 }, (_, c) => f32(o + c * 4));
    // NuSpecialGetRadius reads this local sphere, not the GIZ trigger radius.
    const localSphere = { center: [f32(o + 0xa0), f32(o + 0xa4), f32(o + 0xa8)], radius: f32(o + 0xac) };
    const special = { address: o, name: string(i32(o + 0xb4)), matrix, flags, visible: !!(flags & 2), localSphere, localBounds: { min: [f32(o + 0x80), f32(o + 0x84), f32(o + 0x88)], max: [f32(o + 0x90), f32(o + 0x94), f32(o + 0x98)] }, parts: [] };
    specialObjects.push(special);
    const model = i32(o + 0xb0);
    if (model < modelStart || model >= modelStart + modelCount * 12 || (model - modelStart) % 12) continue;
    const partCount = count(model, 65535), materialList = i32(model + 4), parts = i32(model + 8);
    range(materialList, partCount * 4); range(parts, partCount * 4);
    for (let p = 0; p < partCount; p++) {
      const material = materialArray[u32(materialList + p * 4)];
      const command = commands.get(commandStart + u32(parts + p * 4) * 16);
      if (material && command?.type === 0x82 && meshes.has(command.resource)) {
        const draw = { type: 'mesh', mesh: meshes.get(command.resource), material, matrix, specialIndex: n, visible: !!(flags & 2) };
        draws.push(draw); special.parts.push(draw);
      } else if (material && command?.type === 0x8f) {
        const packet = command.resource; range(packet, 16);
        special.parts.push({ type: 'faceon', address: packet, orientation: u32(packet), faceCount: count(packet + 4),
          vertexBuffer: u32(packet + 8), firstVertex: u32(packet + 12), material, matrix, specialIndex: n, visible: !!(flags & 2) });
      }
    }
  }
  return { buffer, view, range, meshes, materials, textures, vertexBuffers, indexBuffers, draws, specialObjects, splines, splineRecords, unresolvedSplineNames, nameTableDiagnostics,
    summary: { textures: textures.size, materials: materials.size, meshes: meshes.size, draws: draws.length,
      vertexBuffers: vertexBuffers.length, indexBuffers: indexBuffers.length, specialObjects: specialCount, splines: splineRecords.length, unresolvedSplineNames: unresolvedSplineNames.length,
      unterminatedNameTableTails: nameTableDiagnostics.length, externalPointers, blocks: [...blocks.keys()] } };
}

// Face-on packets retain their original center and corner offsets separately:
// the renderer expands the corner along camera-facing axes at runtime. This
// is original geometry data, not a replacement glow mesh.
export function decodeFaceon(scene, packet) {
  if (packet.type !== 'faceon') throw new Error('Expected a face-on packet.');
  const vb = scene.vertexBuffers[packet.vertexBuffer], vertices = packet.faceCount * 6;
  if (!vb || !Number.isSafeInteger(vertices) || vertices < 0 || vertices > 6000000) throw new Error('Invalid face-on vertex count/buffer.');
  const start = vb.offset + packet.firstVertex * 32;
  if (!Number.isSafeInteger(start) || start < vb.offset || start + vertices * 32 > vb.offset + vb.size) throw new Error('Face-on vertex range exceeds its buffer.');
  const positions = new Float32Array(vertices * 3), offsets = new Float32Array(vertices * 2), uvs = new Float32Array(vertices * 2), colors = new Float32Array(vertices * 4);
  function scalar(o) { const v = scene.view.getFloat32(o, true); if (!Number.isFinite(v) || Math.abs(v) > 1e7) throw new Error('Invalid face-on coordinate.'); return v; }
  for (let i = 0; i < vertices; i++) {
    const o = start + i * 32;
    for (let c = 0; c < 3; c++) positions[i * 3 + c] = scalar(o + c * 4);
    for (let c = 0; c < 2; c++) { uvs[i * 2 + c] = scalar(o + 16 + c * 4); offsets[i * 2 + c] = scalar(o + 24 + c * 4); }
    for (let c = 0; c < 4; c++) colors[i * 4 + c] = scene.view.getUint8(o + 12 + (c === 3 ? 3 : 2 - c)) / 255;
  }
  return { positions, offsets, uvs, colors, indices: Uint32Array.from({ length: vertices }, (_, i) => i), orientation: packet.orientation };
}

export function vertexLayout(format, hasTexture = false) {
  if (hasTexture && !(format & 0x3800)) format = (format & 0xffffcfff) | 0x800;
  let offset = 12;
  const normal = (format & 0x880008) ? 2 : (format >>> 2) & 1;
  const tangent = (format & 0x1000020) ? 2 : (format >>> 4) & 1;
  const bitangent = (format & 0x2000080) ? 2 : 0;
  const result = { normalOffset: normal ? 12 : null, normalPacked: normal === 2, colorOffset: null, uvs: [] };
  offset += normal === 1 ? 12 : normal === 2 ? 4 : 0;
  offset += tangent === 1 ? 12 : tangent === 2 ? 4 : 0;
  offset += bitangent === 2 ? 4 : 0;
  if (format & 0x100) { result.colorOffset = offset; offset += 4; }
  if (format & 0x600) offset += 4;
  const half = !!(format & 0x8000000), uvCount = Math.min((format >>> 11) & 7, 4);
  for (let n = 0; n < uvCount; n++) { result.uvs.push({ offset, half }); offset += half ? 4 : 8; }
  result.minimumStride = offset;
  return result;
}

export function decodeMesh(scene, mesh, material) {
  const vb = scene.vertexBuffers[mesh.vertexBuffer], ib = scene.indexBuffers[mesh.indexBuffer];
  if (!vb || !ib || mesh.stride < 12 || mesh.stride > 256 || mesh.type !== 6) throw new Error('Unsupported mesh buffer or topology.');
  const start = vb.offset + mesh.vertexOffset * mesh.stride;
  if (start + mesh.vertices * mesh.stride > vb.offset + vb.size) throw new Error('Vertex range exceeds its buffer.');
  if ((mesh.indexOffset + mesh.triangles + 2) * 2 > ib.size) throw new Error('Index range exceeds its buffer.');
  const layout = vertexLayout(material.format, material.texture >= 0);
  if (layout.minimumStride > mesh.stride) throw new Error('Material vertex format exceeds the mesh stride.');
  const positions = new Float32Array(mesh.vertices * 3), uvs = new Float32Array(mesh.vertices * 2);
  const normals = new Float32Array(mesh.vertices * 3), colors = new Float32Array(mesh.vertices * 3);
  let maximumAbsolutePosition = 0, largeCoordinateVertices = 0;
  // SurfaceUV selects the normal-map channel; diffuse textures use UV set 0.
  const uv = layout.uvs[0];
  function half(value) {
    const s = value & 0x8000 ? -1 : 1, e = (value >>> 10) & 31, m = value & 1023;
    return e === 0 ? s * m * 2 ** -24 : e === 31 ? (m ? NaN : s * Infinity) : s * (1 + m / 1024) * 2 ** (e - 15);
  }
  for (let n = 0; n < mesh.vertices; n++) {
    const o = start + n * mesh.stride;
    let largeCoordinate = false;
    for (let c = 0; c < 3; c++) {
      const position = scene.view.getFloat32(o + c * 4, true);
      if (!Number.isFinite(position)) throw new Error('Invalid vertex position.');
      // Original referenced geometry includes 50-million-unit background
      // cubes and meshes authored above 1e14 units. Preserve those finite
      // coordinates; magnitude is a diagnostic, not proof of corrupt data.
      maximumAbsolutePosition = Math.max(maximumAbsolutePosition, Math.abs(position));
      largeCoordinate ||= Math.abs(position) > 1e7;
      positions[n * 3 + c] = position;
      colors[n * 3 + c] = layout.colorOffset === null ? 1 : scene.view.getUint8(o + layout.colorOffset + (2 - c)) / 255;
      normals[n * 3 + c] = layout.normalOffset === null ? 0 : layout.normalPacked
        ? scene.view.getUint8(o + layout.normalOffset + c) / 127.5 - 1
        : scene.view.getFloat32(o + layout.normalOffset + c * 4, true);
    }
    if (largeCoordinate) largeCoordinateVertices++;
    for (let c = 0; c < 2; c++) {
      uvs[n * 2 + c] = !uv ? 0 : uv.half ? half(scene.view.getUint16(o + uv.offset + c * 2, true)) : scene.view.getFloat32(o + uv.offset + c * 4, true);
    }
  }
  const strip = new Uint16Array(mesh.triangles + 2);
  for (let n = 0; n < strip.length; n++) strip[n] = scene.view.getUint16(ib.offset + (mesh.indexOffset + n) * 2, true);
  const triangles = [];
  for (let n = 2; n < strip.length; n++) {
    let a = strip[n - 2], b = strip[n - 1], c = strip[n];
    if (a >= mesh.vertices || b >= mesh.vertices || c >= mesh.vertices) throw new Error('Vertex index exceeds mesh bounds.');
    if (a === b || b === c || a === c) continue;
    if (n & 1) [a, b] = [b, a];
    triangles.push(a, b, c);
  }
  return { positions, normals, colors, uvs, indices: new Uint32Array(triangles), hasNormals: layout.normalOffset !== null, hasColors: layout.colorOffset !== null,
    coordinateDiagnostics: { largeCoordinateVertices, maximumAbsolutePosition, referenceMagnitude: 1e7 } };
}
